/* Formatting. Every user-visible number passes through here.

   Currency is the reason this file exists. Money is an integer count of the
   minor unit, and how many minor units make a major one is a property of the
   market, not a constant: SAR has 100 halalas, KWD has 1000 fils. A `toFixed(2)`
   hard-coded anywhere in the app is a wrong price rather than an ugly one the
   moment a second country is added, so the divisor is read from the active
   country in lib/geo.ts and converted in exactly one place — here.

   `Intl.NumberFormat` already knows the right number of digits for every
   currency, so the only thing to get right locally is the conversion from minor
   units, and to do it once. */

import type { LocalizedText, Money } from './types'
import { cityLabelForDistrict, districtLabel, getCountry } from './geo'
import { formatAnyPhone } from './phone'

export type Locale = 'en' | 'ar'

const country = getCountry()

export const CURRENCY = country.currency

/** Minor units per major unit for the active currency. SAR is 100. */
const MINOR = country.minorUnits

/** `100` → 2, `1000` → 3. The digit count `Intl` will render, derived rather
 *  than restated so the two can never disagree. */
const FRACTION_DIGITS = Math.round(Math.log10(MINOR))

/* Intl formatters are expensive to construct and cheap to reuse. Building one
   per render inside a price list is a measurable cost on a long menu. */
const moneyFmt = new Map<string, Intl.NumberFormat>()

function fmt(locale: Locale, opts: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(opts)}`
  let f = moneyFmt.get(key)
  if (!f) {
    /* `ar-SA` renders Eastern Arabic numerals, which is correct for the locale
       but is a genuine readability problem for prices when the rest of the app
       shows Latin digits in phone numbers and order refs. The `-u-nu-latn`
       subtag on the country's number tag keeps Arabic currency placement and
       separators with Latin digits. */
    f = new Intl.NumberFormat(country.numberTag[locale], {
      style: 'currency',
      currency: CURRENCY,
      ...opts,
    })
    moneyFmt.set(key, f)
  }
  return f
}

/* Some currencies have no short symbol in ICU and render as the bare ISO code.
   SAR is not one of them — `en-SA` gives "SAR 45.00" and `ar-SA` gives "ر.س",
   both of which are what Saudi actually writes — so nothing is overridden here.
   The hook stays because it is the seam a market like Kuwait needs (ICU renders
   "KWD 8.000" where the country writes "KD 8.000"), and because doing it through
   `formatToParts` is the only safe way: a naive `.replace('SAR', 'SR')` would
   also rewrite a promo code or an order reference that happened to contain it. */
const SHORT_SYMBOL: Record<Locale, string | null> = { en: null, ar: null }

/** `4500` → `SAR 45.00`. */
export function money(minor: Money, locale: Locale = 'en'): string {
  const short = SHORT_SYMBOL[locale]
  const parts = fmt(locale).formatToParts(minor / MINOR)

  return parts.map((p) => (p.type === 'currency' && short ? short : p.value)).join('')
}

/** Same, without the currency symbol — for tables where the column is labelled. */
export function moneyBare(minor: Money, locale: Locale = 'en'): string {
  return fmt(locale, {
    style: 'decimal',
    minimumFractionDigits: FRACTION_DIGITS,
    maximumFractionDigits: FRACTION_DIGITS,
  }).format(minor / MINOR)
}

export function toMinor(major: number): Money {
  return Math.round(major * MINOR)
}

/** Repeated currency glyphs for a 1–3 price level, the `$`/`$$`/`$$$` idiom.
 *  Uses the compact two-letter form rather than the ISO code: "SAR SAR SAR" is
 *  noise, "SR SR SR" reads as a scale. */
export function priceLevel(level: 1 | 2 | 3): string {
  return Array.from({ length: level }, () => 'SR').join(' ')
}

/* ── time ─────────────────────────────────────────────────────────────────── */

/** `36` → `1.5`, `48` → `2`. Whole days lose the decimal; a turnaround shown
 *  as "2.0 days" reads as a measurement rather than a promise. */
function asDays(minutes: number): string {
  const v = minutes / (24 * 60)
  return Number.isInteger(v) ? String(v) : v.toFixed(1)
}

function asHours(minutes: number): number {
  return Math.round(minutes / 60)
}

/** `[24*60, 36*60]` → `1–1.5 days`; `[60, 180]` → `1–3 hrs`. */
export function eta(range: [number, number] | null, locale: Locale = 'en'): string | null {
  if (!range) return null
  const [lo, hi] = range

  if (lo >= 24 * 60) {
    return locale === 'ar' ? `${asDays(lo)}–${asDays(hi)} يوم` : `${asDays(lo)}–${asDays(hi)} days`
  }
  return locale === 'ar' ? `${asHours(lo)}–${asHours(hi)} ساعة` : `${asHours(lo)}–${asHours(hi)} hrs`
}

const dateFmt = new Map<string, Intl.DateTimeFormat>()

function dtf(locale: Locale, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(opts)}`
  let f = dateFmt.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat(country.dateTag[locale], opts)
    dateFmt.set(key, f)
  }
  return f
}

/** `2026-08-14` → `Fri 14 Aug`. */
export function dayLabel(iso: string, locale: Locale = 'en'): string {
  return dtf(locale, { weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(iso))
}

/** `2026-08-14` → `Today` / `Tomorrow` / `Fri 14 Aug`. */
export function relativeDay(iso: string, locale: Locale = 'en', now = new Date()): string {
  const target = new Date(`${iso}T00:00:00`)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000)
  if (days === 0) return locale === 'ar' ? 'اليوم' : 'Today'
  if (days === 1) return locale === 'ar' ? 'غداً' : 'Tomorrow'
  return dayLabel(iso, locale)
}

function to12Hour(hhmm: string): { text: string; suffix: 'AM' | 'PM' } {
  const [h = '0', m = '00'] = hhmm.split(':')
  const hour = Number(h)
  const h12 = hour % 12 === 0 ? 12 : hour % 12
  return { text: `${h12}:${m}`, suffix: hour >= 12 ? 'PM' : 'AM' }
}

/** `09:00`–`11:00` → `9:00 – 11:00 AM`.
 *
 *  The shared meridiem collapses to one instance. "9:00 AM – 11:00 AM" is
 *  correct and reads as two separate times; a slot is one span. Arabic keeps
 *  24-hour, which is what the locale actually uses for appointments. */
export function slotLabel(from: string, to: string, locale: Locale = 'en'): string {
  if (locale === 'ar') return `${from} – ${to}`

  const a = to12Hour(from)
  const b = to12Hour(to)
  return a.suffix === b.suffix
    ? `${a.text} – ${b.text} ${b.suffix}`
    : `${a.text} ${a.suffix} – ${b.text} ${b.suffix}`
}

/** Coarse "how long ago" for review timestamps. */
export function timeAgo(iso: string, locale: Locale = 'en', now = new Date()): string {
  const diff = now.getTime() - new Date(iso).getTime()
  const days = Math.floor(diff / 86_400_000)
  const rtf = new Intl.RelativeTimeFormat(locale === 'ar' ? 'ar' : 'en', { numeric: 'auto' })
  if (days < 1) return rtf.format(-Math.floor(diff / 3_600_000), 'hour')
  if (days < 30) return rtf.format(-days, 'day')
  if (days < 365) return rtf.format(-Math.floor(days / 30), 'month')
  return rtf.format(-Math.floor(days / 365), 'year')
}

/* ── text ─────────────────────────────────────────────────────────────────── */

/** Pick the active language out of a `LocalizedText`. Falls back to English
 *  rather than rendering `undefined` if a translation is ever missing. */
export function t(text: LocalizedText | undefined, locale: Locale): string {
  if (!text) return ''
  return (locale === 'ar' ? text.ar : text.en) || text.en
}

/** `92` → `4.6` style one-decimal rating, never `4.60` or `5.0` → `5`. */
export function rating(n: number): string {
  return n.toFixed(1)
}

/** `2314` → `2.3k`. Review counts past a thousand are noise at full precision. */
export function compactCount(n: number, locale: Locale = 'en'): string {
  return new Intl.NumberFormat(country.numberTag[locale], {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n)
}

/** `+966 50 123 4567` — a number, grouped for reading.
 *
 *  Delegates to `lib/phone`, which picks the shape off the number's **own**
 *  dial code. This used to format against `getCountry()` unconditionally and
 *  prepend `+966`, so a Pakistani account rendered under the Saudi dial code
 *  with its leading digit truncated away. See the header of that module.
 *
 *  There is no national-only counterpart any more. `phone()` existed for forms
 *  that were single-market by design, and there are none left: every field that
 *  takes a number carries the country picker, and every field that shows one is
 *  showing a number whose country is a fact about the number. */
export function phoneIntl(raw: string): string {
  return formatAnyPhone(raw)
}

/** Wrap a Latin run so the bidi algorithm leaves it alone.
 *
 *  `<bdi>` is the usual answer, but it cannot travel through an i18next
 *  interpolation — the value lands as text inside an Arabic sentence, and by
 *  then it is too late for markup. U+2068 FIRST STRONG ISOLATE and U+2069 POP
 *  DIRECTIONAL ISOLATE do the identical job at the character level, which
 *  survives being passed as a plain string. Without it "+966 50 123 4567"
 *  renders as "7654 321 05 669+" inside Arabic copy. */
export function isolate(text: string): string {
  return `⁨${text}⁩`
}

/* ── addresses ────────────────────────────────────────────────────────────── */

const ADDR_WORDS = {
  en: { building: 'Building', floor: 'Floor', apartment: 'Apt' },
  ar: { building: 'مبنى', floor: 'دور', apartment: 'شقة' },
} as const

/** One-line courier-readable address, in Saudi National Address order:
 *  building number, then street, then district, then city. That is the order a
 *  driver reads it in — narrowing from the door outwards — not schema order.
 *
 *  The district is stored as a stable key and localised on the way out, so an
 *  address saved in English still renders in Arabic without being re-entered. */
export function addressLine(
  a: {
    area: string
    street: string
    building: string
    floor?: string
    apartment?: string
    additionalNo?: string
  },
  locale: Locale = 'en',
): string {
  const w = ADDR_WORDS[locale]
  const city = cityLabelForDistrict(a.area, locale)

  const parts = [
    `${w.building} ${a.building}`,
    a.street,
    districtLabel(a.area, locale),
    city || null,
    a.floor ? `${w.floor} ${a.floor}` : null,
    a.apartment ? `${w.apartment} ${a.apartment}` : null,
  ].filter(Boolean)

  return parts.join(locale === 'ar' ? '، ' : ', ')
}
